// =============================================================================
// components/AnimatedLogo.tsx — the animated CRYB mark.
//
// Two modes, both SVG (react-native-svg is a proven dep — Sparkline/ParticleBg):
//   • 'fill'    — the CRYB wordmark FILLS bottom→top with the brand gradient over
//                 a faint outline. Used by the splash as a "loading" logo; drive
//                 it with `progress` (0→1) for real load progress, or leave it
//                 self-animating (auto-fills, then a soft breathing shimmer).
//   • 'shimmer' — a subtle continuous highlight sweeps across a solid brand mark.
//                 Lightweight; safe to drop into headers app-wide.
//
// Light mode by default (brand purple on the app's lavender canvas). Never throws
// — if reanimated/SVG hiccup it still renders a static gradient wordmark.
// =============================================================================
import React, { useEffect } from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import Svg, { Defs, LinearGradient, Stop, Mask, Rect, Text as SvgText } from 'react-native-svg';
import Animated, {
  useSharedValue, useAnimatedProps, withTiming, withRepeat, withDelay, Easing, interpolate,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { ThemeMode } from '../constants/theme';

const AnimatedRect = Animated.createAnimatedComponent(Rect);
const AnimatedLinearGradient = Animated.createAnimatedComponent(LinearGradient);

export interface AnimatedLogoProps {
  /** Cap height of the wordmark in px. Default 64. */
  size?: number;
  /**
   * 'fill'    — rises bottom→top (loading/splash).
   * 'shimmer' — subtle highlight sweep over a solid mark.
   * 'flow'    — the brand gradient (purple→blue→white) drifts through the
   *             letters forever. The app-wide header mark.
   * Default 'fill'.
   */
  mode?: 'fill' | 'shimmer' | 'flow';
  /** Optional external 0→1 fill progress (a shared value). If omitted, self-animates. */
  progress?: SharedValue<number>;
  /** Light (default) or dark theme — only affects the faint outline tint. */
  theme?: ThemeMode;
  style?: StyleProp<ViewStyle>;
}

const WORD = 'CRYB';
const G1 = '#6C5CE7';
const G2 = '#A855F7';
// Flow palette — brand purples ONLY. No blue, no white: the white was reading as
// a cheap "shine" and the blue was off-brand. The mark just drifts through
// light lavender → brand purple → violet, an elegant gradient shift.
const F_DEEP = '#6C5CE7';   // brand purple
const F_MID = '#7C4DFF';    // violet
const F_LIGHT = '#A78BFA';  // light lavender

export default function AnimatedLogo({ size = 64, mode = 'fill', progress, theme = 'light', style }: AnimatedLogoProps) {
  // Box: wide enough for CRYB at the given cap height, with breathing room.
  const H = size * 1.35;
  const W = size * 4.2;
  const baselineY = H * 0.72;
  const fontSize = size;

  // Self-driven fill: 0→1 fill, then a gentle loop shimmer of the fill line.
  const internal = useSharedValue(0);
  const sweep = useSharedValue(0);
  const flow = useSharedValue(0); // 0→1 drift phase for 'flow' mode (seamless loop)
  useEffect(() => {
    if (!progress) {
      internal.value = withDelay(120, withTiming(1, { duration: 1500, easing: Easing.out(Easing.cubic) }));
    }
    // continuous highlight sweep (shimmer mode + a soft post-fill shimmer)
    sweep.value = withRepeat(withTiming(1, { duration: 1600, easing: Easing.inOut(Easing.quad) }), -1, false);
    // continuous gradient drift (flow mode) — linear so the loop is seamless.
    // Slow + gentle (the fast drift read like a cheap "shine" sweeping across).
    flow.value = withRepeat(withTiming(1, { duration: 8000, easing: Easing.linear }), -1, false);
    return undefined;
  }, [progress]);

  const p = progress ?? internal;

  // Fill mask: a white rect that rises from the baseline, revealing the gradient.
  const fillMaskProps = useAnimatedProps(() => {
    const v = Math.max(0, Math.min(1, p.value));
    return { y: H * (1 - v), height: H * v };
  });

  // Shimmer: a narrow bright band sweeping left→right across the mark.
  const shimmerProps = useAnimatedProps(() => {
    const x = interpolate(sweep.value, [0, 1], [-W * 0.4, W]);
    return { x } as any;
  });

  // Flow: drift a wide (2W-span, period-0.5) gradient across the letters. The
  // rect [0,W] stays fully inside [x1,x2]=[s-W, s+W] for s∈[0,W]; advancing s by
  // W shifts the stop parameter by exactly one period → a seamless infinite loop.
  const flowProps = useAnimatedProps(() => {
    const s = flow.value * W;
    return { x1: s - W, x2: s + W } as any;
  });

  const outlineOpacity = theme === 'dark' ? 0.22 : 0.14;

  return (
    <View style={[styles.wrap, { width: W, height: H }, style]} accessibilityRole="header" accessibilityLabel="CRYB">
      <Svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <Defs>
          <LinearGradient id="crybBrand" x1="0" y1="0" x2="1" y2="1">
            <Stop offset="0" stopColor={G1} />
            <Stop offset="1" stopColor={G2} />
          </LinearGradient>
          {/* Flowing all-purple gradient (light→brand→violet). Periodic (period 0.5
              across the 2W span) so drifting by one period loops with no seam.
              userSpaceOnUse so x1/x2 are pixel coords we can animate. */}
          <AnimatedLinearGradient id="crybFlow" gradientUnits="userSpaceOnUse" y1={0} y2={0} animatedProps={flowProps}>
            <Stop offset="0" stopColor={F_DEEP} />
            <Stop offset="0.125" stopColor={F_LIGHT} />
            <Stop offset="0.25" stopColor={F_MID} />
            <Stop offset="0.375" stopColor={F_LIGHT} />
            <Stop offset="0.5" stopColor={F_DEEP} />
            <Stop offset="0.625" stopColor={F_LIGHT} />
            <Stop offset="0.75" stopColor={F_MID} />
            <Stop offset="0.875" stopColor={F_LIGHT} />
            <Stop offset="1" stopColor={F_DEEP} />
          </AnimatedLinearGradient>
          <LinearGradient id="crybShine" x1="0" y1="0" x2="1" y2="0">
            <Stop offset="0" stopColor="#FFFFFF" stopOpacity="0" />
            <Stop offset="0.5" stopColor="#FFFFFF" stopOpacity="0.65" />
            <Stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
          </LinearGradient>
          <Mask id="fillMask">
            <AnimatedRect x={0} width={W} fill="#FFFFFF" animatedProps={fillMaskProps} />
          </Mask>
          <Mask id="wordMask">
            <SvgText x={W / 2} y={baselineY} textAnchor="middle" fontSize={fontSize} fontWeight="800" fill="#FFFFFF" letterSpacing={size * 0.03}>{WORD}</SvgText>
          </Mask>
        </Defs>

        {/* Faint empty outline (what's "not filled yet") */}
        <SvgText x={W / 2} y={baselineY} textAnchor="middle" fontSize={fontSize} fontWeight="800" fill={G1} fillOpacity={outlineOpacity} letterSpacing={size * 0.03}>{WORD}</SvgText>

        {mode === 'fill' ? (
          // Gradient wordmark revealed by the rising fill mask.
          <SvgText x={W / 2} y={baselineY} textAnchor="middle" fontSize={fontSize} fontWeight="800" fill="url(#crybBrand)" letterSpacing={size * 0.03} mask="url(#fillMask)">{WORD}</SvgText>
        ) : mode === 'flow' ? (
          // Letters filled with the endlessly drifting purple→blue→white gradient.
          <SvgText x={W / 2} y={baselineY} textAnchor="middle" fontSize={fontSize} fontWeight="800" fill="url(#crybFlow)" letterSpacing={size * 0.03}>{WORD}</SvgText>
        ) : (
          // Solid gradient wordmark (shimmer sweeps over it).
          <SvgText x={W / 2} y={baselineY} textAnchor="middle" fontSize={fontSize} fontWeight="800" fill="url(#crybBrand)" letterSpacing={size * 0.03}>{WORD}</SvgText>
        )}

        {/* Highlight sweep, clipped to the letters (fill/shimmer only) */}
        {mode !== 'flow' && (
          <AnimatedRect y={0} width={W * 0.4} height={H} fill="url(#crybShine)" mask="url(#wordMask)" animatedProps={shimmerProps} />
        )}
      </Svg>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { alignItems: 'center', justifyContent: 'center' },
});
